import * as React from 'react';
import { css } from '@emotion/react';
import { FixMeLater } from '../../utils';
import { useHoneycombTheme, TypographyBodySize, TextColorOption } from '../../theming';
import { ColorOption } from '../../theming/types/HoneycombPalette';

export type AvatarSize = 's' | 'm' | 'l' | 'xl' | '2xl';
export type AvatarColor = ColorOption;

export type AvatarTheme = {
    size: {
        [key in AvatarSize]: {
            height: number;
            fontSize: TypographyBodySize;
        };
    };
    iconSizeCoefficient: number;
};

const avatarTheme: AvatarTheme = {
    size: {
        s: {
            height: 20,
            fontSize: 'xs',
        },
        m: {
            height: 24,
            fontSize: 'xs',
        },
        l: {
            height: 32,
            fontSize: 'm',
        },
        xl: {
            height: 40,
            fontSize: 'l',
        },
        '2xl': {
            height: 48,
            fontSize: 'xl',
        },
    },
    iconSizeCoefficient: 0.6,
};

export type AvatarProps = {
    /**
     * The content of the component.
     */
    children: React.ReactNode;

    /**
     * The colour variant of the avatar.
     * @default default
     */
    color?: AvatarColor;

    /**
     * The colour variant of the avatar.
     * @default default
     */
    textColor?: TextColorOption;

    /**
     * The size variant of the avatar.
     * @default m
     */
    size?: AvatarSize;
};

/**
 * The Avatar component allows us to display things like text, icons or any custom
 * React elements inside it. It has various colors, sizes and text size support built in controlled
 * from props. It is typically used as a user icon for logged in users or alongisde user-generated content.
 */
export const Avatar: React.ForwardRefExoticComponent<AvatarProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Avatar(props: AvatarProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            color: colorProp = 'neutral.tint.100',
            textColor: textColorProp,
            size: sizeProp = 'm',
        } = props;

        const size = avatarTheme.size[sizeProp];
        const { theme } = useHoneycombTheme();
        const { typography } = theme;

        const childSize = size.height * avatarTheme.iconSizeCoefficient;

        const color = theme.fn.getColor(colorProp);
        const textColor = textColorProp ? theme.fn.getColor(textColorProp) : theme.fn.getContrastTextColor(color);

        const styles = css`
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: ${size.height}px;
            height: ${size.height}px;
            background-color: ${color};
            color: ${textColor};
            border-radius: 50%;
            font-size: ${typography.variant.body[size.fontSize].l.fontSize};
            font-weight: ${typography.family.body.fontWeight.semiBold};
            > [data-component='svg-icon'] {
                width: ${childSize}px;
                height: ${childSize}px;
                fill: ${textColor};
            }
            overflow: visible;
        `;

        return (
            <div ref={ref} css={styles}>
                {children}
            </div>
        );
    }
);
